import useClickTimeline from "../../../hooks/useClickTimeline";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BarChart3, ChevronDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, BarChart, Bar } from 'recharts';
import { useClickOutside } from "../../../hooks/ClickoutsideHook";
import NoDataFound from "../dataDisplay/NoDataFound";
/**
 * Displays tap activity data grouped by country or city in a horizontal bar chart.
 *
 * @param {object} props - The component props.
 * @param {string} props.eventId - The unique ID of the event to fetch analytics for.
 * @param {string} props.token - The user authentication token (passed to the hook for dependency).
 * @returns {React.ReactElement} The Geographic Distribution chart component.
 */

// Geographic Distribution (now uses API data)
const GeographicChart = ({ eventId, token }) => {
    const { data: raw, loading, error } = useClickTimeline({ eventId, token });
    const [geoView, setGeoView] = useState('country');

    // Build Intl region name resolver once
    const regionFmt = useMemo(() => {
        try { return new Intl.DisplayNames(['en'], { type: 'region' }); } catch (_) { return null; }
    }, []);

    const cityData = useMemo(
        () => Object.entries(raw?.cityData || {}).map(([name, value]) => ({ name, value: Number(value) || 0 })),
        [raw]
    );

    const countryData = useMemo(
        () => Object.entries(raw?.countryData || {}).map(([code, value]) => ({
            name: code === 'Unknown' ? 'Unknown' : (regionFmt?.of(code) || code),
            code,
            value: Number(value) || 0,
        })),
        [raw, regionFmt]
    );
    const CustomBarTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-[var(--analytics-card-color)]  border border-gray-600 rounded-lg p-3 shadow-lg">
                    <p className="text-black text-sm font-medium">{payload[0].payload.name}</p>
                    <p className="text-orange-400 text-sm">
                        {`Taps: ${payload[0].value}`}
                    </p>
                    {geoView === 'country' && payload[0].payload.code !== 'Unknown' && (
                        <p className="text-black text-xs mt-1">
                            {`Country Code: ${payload[0].payload.code}`}
                        </p>
                    )}
                </div>
            );
        }
        return null;
    };

    const currentData = geoView === 'country' ? countryData : cityData;

    const GeoViewSelector = () => {
        const [isOpen, setIsOpen] = useState(false);
        const ref = useRef(null);
        const options = [
            { value: 'country', label: 'By Country' },
            { value: 'city', label: 'By City' },
        ];
        useClickOutside(ref, () => setIsOpen(false));
        const currentOption = options.find((opt) => opt.value === geoView);

        return (
            <div className="relative" ref={ref}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="bg-var(--analytics-card-color)  text-black px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors border border-gray-600 cursor-pointer"
                >
                    <span className="text-sm font-medium">{currentOption?.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                    <div className="absolute top-full mt-2 right-0 bg-var(--analytics-card-color) border border-gray-600 rounded-lg shadow-lg z-10 min-w-[140px]">
                        {options.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => {
                                    setGeoView(option.value);
                                    setIsOpen(false);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-300 transition-colors first:rounded-t-lg last:rounded-b-lg ${geoView === option.value ? 'bg-gray-300 text-orange-400' : 'text-black'}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };
    const isDataEmpty = !loading && !error && currentData.length === 0;

    return (
        <div className="bg-transparent border border-[var(--border-color2)] rounded-lg  p-6 relative">
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-black">Geographic Distribution</h3>
                    <p className="text-black text-sm">Tap activity by {geoView === 'country' ? 'country' : 'city'}</p>
                </div>
                <GeoViewSelector />
            </div>
            {loading && <div className="h-96 flex items-center justify-center text-gray-400 text-sm">
                <div className="loader"></div>
            </div>}

            {error && !loading && <div className="h-96 flex items-center justify-center text-red-400 text-sm">{error}</div>}
            {isDataEmpty && (
                <div className="h-96">
                    <NoDataFound
                        title="No Geographic Activity"
                        icon={BarChart3}
                        message={`We haven't collected any location data for the current ${geoView} view. As soon as the first click registers, data will appear here.`}
                        height="h-full"
                    />
                </div>
            )}
            {!loading && !error && currentData.length > 0 && (
                <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%" >
                        <BarChart data={currentData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }} layout="vertical" >
                            <CartesianGrid strokeDasharray="1 1" stroke="#333333" horizontal={false} vertical={true} />
                            <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 11 }} domain={[0, 'auto']} />
                            <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#000000', fontSize: 12 }} width={100} className='bg-red-400' />
                            <Tooltip
                                content={<CustomBarTooltip />}
                                cursor={{ fill: 'transparent' }}
                            />
                            <Bar dataKey="value" fill="#ff6b35" radius={[0, 4, 4, 0]} barSize={30} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

        </div>
    );
};

export default GeographicChart;